import { showSavePremiereProjectDialog } from '@mimir/web/dialogs/savePremiereProject';
import { getGlobalStore } from '@mimir/web/store';
import { copyFile, copyFileSync, isDirAvailableLocally, isFileAvailableLocally } from '@mimir/common/util/fs';
import { getBasenameWithoutExtension, getExtname } from '@mimir/common/platformAbstraction/pathComposition';
import { evalScript } from './cep';
import { getSanitizedFileNameForScript, makeDir } from './fileHelpers';
import type { PremiereProject } from './interfaces';
import { getPathForScript } from './misc';
import moment from 'moment';
import { getElectronShell } from '@mimir/web/desktop/utils';
import { getTempDirInUserDataWith } from '@mimir/client-common/upload/premiere';
import type { StorageProfile } from '@mimir/web/desktop/storageSettings';
import { exceptionToString } from '@mimir/common/util/exception';
import { ConsoleLogger } from '@mimir/common/util/logging';
import { type AdobePanelContext, type ElectronContext, isInPremiere } from '@mimir/common/context/applicationContext';
import type { NotificationOps } from '@mimir/client-common/fileSync/notificationOps';
import type { Logger } from '@mimir/common/types/logger';
import { setCurrentProjectMimirItemIdMetadata } from './saveProjectUtils';

export const DEFAULT_PREMIERE_PROJECT_FOLDER = 'Adobe Premiere Projects';

export const getProjectItemIdSuffix = (itemId: string) => {
  return `_${itemId.slice(-8)}`;
};

// windows doesn't allow ':' or '/' in filenames, so we use
// YYYYMMDDHHmmss format as suffix instead of direct date
export const getProjectNameSuffix = (itemId: string, isProjectTemplate: boolean) => {
  const timestamp = moment(new Date()).format('YYYYMMDDHHmmss');
  if (isProjectTemplate) return `_${timestamp}`;
  return `${getProjectItemIdSuffix(itemId)}_${timestamp}`;
};

async function getTempProjectLocation(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
  },
  userDataSystemPath: string,
): Promise<string> {
  return (await getTempDirInUserDataWith(services, userDataSystemPath)) + services.applicationContext.pathSep + DEFAULT_PREMIERE_PROJECT_FOLDER;
}

export const openProjectFile = async (
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
  },
  config: {
    downloadPath: string,
    userDataSystemPath: string,
  },
  filePath: string,
  title: string,
  itemId: string,
  suppressWarning = false,
  isProjectTemplate = false,
) => {
  try {
    const projectIdentifier = getProjectNameSuffix(itemId, isProjectTemplate);
    const pathSep = services.applicationContext.pathSep;
    const ext = getExtname(filePath, pathSep);
    const sanitizedTitle = getSanitizedFileNameForScript(services.applicationContext, title);
    const shortName = getBasenameWithoutExtension(sanitizedTitle, pathSep);
    const projectName = `${shortName}${projectIdentifier}${ext}`;

    const selectedStorageProfile = getGlobalStore().getters.selectedStorageProfile as StorageProfile | undefined;
    let targetBaseFolder = selectedStorageProfile?.projectDownloadPath;

    if (!targetBaseFolder) {
      targetBaseFolder = await getTempProjectLocation(services, config.userDataSystemPath);
    }

    if (!(await isDirAvailableLocally(services.applicationContext.nodeApi, services.logger, targetBaseFolder))) {
      await makeDir(services, targetBaseFolder);
    }

    const targetProjectPath = targetBaseFolder + pathSep + projectName;

    if (isInPremiere(services.applicationContext)) {
      await openProjectInPremiere(services.applicationContext, filePath, targetProjectPath);

      // Store the Mimir item ID in the project's root bin metadata
      // This allows us to track which Mimir item this project was opened from
      await setCurrentProjectMimirItemIdMetadata(services.applicationContext, itemId);
    } else {
      await openProjectInElectron(services.applicationContext, filePath, targetProjectPath, suppressWarning ? undefined : (msg) => services.notificationOps.displayBriefSuccess(msg));
    }
  } catch(err: any) {
    const error = exceptionToString(err);
    services.logger.error(`Error in openProjectFile: ${exceptionToString(error)}`);
    services.notificationOps.displayBriefError(`Failed to open project file ${title}. ${error}`);
  }
};

const openProjectInPremiere = async (adobeContext: AdobePanelContext, sourcePath: string, targetPath: string) => {
  const isProjectAlreadySaved = await isFileAvailableLocally(adobeContext.nodeApi, new ConsoleLogger(), targetPath);

  // If not available in the target path, copy it there first
  if (!isProjectAlreadySaved) {
    await copyFile(adobeContext.nodeApi, sourcePath, targetPath);
  }

  // Open from target path
  const transformedTargetPath = getPathForScript(adobeContext, targetPath);
  const script = `openFileAsPremiereProject("${transformedTargetPath}")`;

  evalScript(script);
};

const openProjectInElectron = async (applicationContext: ElectronContext, sourcePath: string, targetPath: string, notifyUserFunc?: (msg: string) => void) => {
  const shell = getElectronShell(applicationContext);
  if (shell) {
    const appName = targetPath.endsWith('.drp') ? 'DaVinci Resolve' : 'Premiere Pro';
    if (notifyUserFunc) notifyUserFunc(`Opening project in ${appName}...`);
    await copyFile(applicationContext.nodeApi, sourcePath, targetPath);
    shell.openPath(targetPath);
  }
};

export const uploadCurrentProjectToMimir = async (applicationContext: AdobePanelContext | ElectronContext, folderId?: string) => {
  const currentProject = await applicationContext.cepBridgeClient.call('getActiveProjectBasicInfo');
  await saveProject(applicationContext, currentProject);
  showSavePremiereProjectDialog(currentProject, folderId);
};

export const saveProject = async (applicationContext: AdobePanelContext | ElectronContext, project: PremiereProject): Promise<void> => {
  await applicationContext.cepBridgeClient.call('saveProject', project.path);
};

export const copyProjectToMediaLocation = (applicationContext: AdobePanelContext | ElectronContext, projectPath: string, itemId: string) => {
  try {
    const store = getGlobalStore();
    if (!store.getters.downloadPath) {
      console.debug('copyProjectToMediaLocation: skipping since download path is not configured yet');
      return;
    }

    const pathWithItemId = store.getters.downloadPath + applicationContext.pathSep + itemId + '.prproj';
    console.debug(`copying project ${projectPath} to final location ${pathWithItemId}`);
    copyFileSync(applicationContext.nodeApi, projectPath, pathWithItemId);
  } catch (err) {
    console.debug(`error copying project ${projectPath} to final location: ${err}`);
  }
};
